import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Cookie, Settings } from "lucide-react";

const CookieBanner = () => {
  const [visible, setVisible] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // siempre activas
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const consent = localStorage.getItem("cookie_consent");
    if (!consent) {
      const timer = setTimeout(() => setVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const acceptAll = () => {
    const consent = { essential: true, analytics: true, marketing: true, date: new Date().toISOString() };
    localStorage.setItem("cookie_consent", JSON.stringify(consent));
    setVisible(false);
  };

  const rejectAll = () => {
    const consent = { essential: true, analytics: false, marketing: false, date: new Date().toISOString() };
    localStorage.setItem("cookie_consent", JSON.stringify(consent));
    setVisible(false);
  };

  const savePreferences = () => {
    const consent = { ...preferences, date: new Date().toISOString() };
    localStorage.setItem("cookie_consent", JSON.stringify(consent));
    setVisible(false);
    setShowConfig(false);
  };

  if (!visible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="fixed bottom-0 left-0 right-0 z-[100] p-4 md:p-6"
        data-testid="cookie-banner"
      >
        <div className="max-w-4xl mx-auto bg-[#111111] border border-white/10 rounded-2xl p-6 md:p-8 shadow-2xl">
          {!showConfig ? (
            <>
              <div className="flex items-start gap-4 mb-6">
                <Cookie className="text-[#D4AF37] shrink-0 mt-1" size={24} />
                <div>
                  <h3 className="text-white font-medium text-lg mb-2">Utilizamos cookies</h3>
                  <p className="text-[#A1A1AA] text-sm leading-relaxed">
                    Usamos cookies propias y de terceros para mejorar tu experiencia, analizar el uso de la web y mostrar contenido personalizado. 
                    Puedes aceptar todas, rechazar las no esenciales o configurar tus preferencias.{" "}
                    <button
                      onClick={() => window.dispatchEvent(new CustomEvent("open-legal", { detail: "cookies" }))}
                      className="text-[#D4AF37] hover:underline"
                      data-testid="cookie-policy-link"
                    >
                      Política de cookies
                    </button>
                  </p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={acceptAll}
                  className="flex-1 bg-gradient-to-r from-[#D4AF37] to-[#E5C158] text-[#050505] font-semibold py-3 px-6 rounded-full text-sm hover:shadow-lg hover:shadow-[#D4AF37]/20 transition-all"
                  data-testid="cookie-accept-all"
                >
                  Aceptar todas
                </button>
                <button
                  onClick={rejectAll}
                  className="flex-1 bg-transparent border border-white/20 text-white font-medium py-3 px-6 rounded-full text-sm hover:border-white/40 transition-all"
                  data-testid="cookie-reject-all"
                >
                  Rechazar no esenciales
                </button>
                <button
                  onClick={() => setShowConfig(true)}
                  className="flex-1 bg-transparent border border-white/10 text-[#A1A1AA] font-medium py-3 px-6 rounded-full text-sm hover:text-white hover:border-white/20 transition-all flex items-center justify-center gap-2"
                  data-testid="cookie-configure"
                >
                  <Settings size={16} />
                  Configurar
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-white font-medium text-lg">Configurar cookies</h3>
                <button onClick={() => setShowConfig(false)} className="text-[#A1A1AA] hover:text-white">
                  <X size={20} />
                </button>
              </div>

              <div className="space-y-4 mb-6">
                <div className="flex items-center justify-between p-4 rounded-xl border border-white/10 bg-white/[0.02]">
                  <div>
                    <p className="text-white font-medium text-sm">Cookies esenciales</p>
                    <p className="text-[#A1A1AA] text-xs mt-1">Necesarias para el funcionamiento de la web. No se pueden desactivar.</p>
                  </div>
                  <div className="w-10 h-6 bg-[#D4AF37] rounded-full flex items-center justify-end px-1">
                    <div className="w-4 h-4 bg-white rounded-full"></div>
                  </div>
                </div>

                <div
                  className="flex items-center justify-between p-4 rounded-xl border border-white/10 bg-white/[0.02] cursor-pointer"
                  onClick={() => setPreferences((p) => ({ ...p, analytics: !p.analytics }))}
                  data-testid="cookie-toggle-analytics"
                >
                  <div>
                    <p className="text-white font-medium text-sm">Cookies analíticas</p>
                    <p className="text-[#A1A1AA] text-xs mt-1">Nos ayudan a entender cómo se usa la web para mejorarla.</p>
                  </div>
                  <div className={`w-10 h-6 rounded-full flex items-center px-1 transition-colors ${preferences.analytics ? "bg-[#D4AF37] justify-end" : "bg-white/20 justify-start"}`}>
                    <div className="w-4 h-4 bg-white rounded-full"></div>
                  </div>
                </div>

                <div
                  className="flex items-center justify-between p-4 rounded-xl border border-white/10 bg-white/[0.02] cursor-pointer"
                  onClick={() => setPreferences((p) => ({ ...p, marketing: !p.marketing }))}
                  data-testid="cookie-toggle-marketing"
                >
                  <div>
                    <p className="text-white font-medium text-sm">Cookies de marketing</p>
                    <p className="text-[#A1A1AA] text-xs mt-1">Permiten mostrarte contenido y publicidad relevante.</p>
                  </div>
                  <div className={`w-10 h-6 rounded-full flex items-center px-1 transition-colors ${preferences.marketing ? "bg-[#D4AF37] justify-end" : "bg-white/20 justify-start"}`}>
                    <div className="w-4 h-4 bg-white rounded-full"></div>
                  </div>
                </div>
              </div>

              <button
                onClick={savePreferences}
                className="w-full bg-gradient-to-r from-[#D4AF37] to-[#E5C158] text-[#050505] font-semibold py-3 px-6 rounded-full text-sm hover:shadow-lg hover:shadow-[#D4AF37]/20 transition-all"
                data-testid="cookie-save-preferences"
              >
                Guardar preferencias
              </button>
            </>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CookieBanner;
